import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { FairUsePolicyFields } from '../../components/fairUse/FairUsePolicyFields';
import type { FairUsePolicy } from '../../lib/api/domains/customerCare';

/**
 * Fair-Use-Vereinbarung eines Kunden festschreiben.
 *
 * Zwei Ebenen: Solange hier nichts gespeichert ist, gilt der Hausstandard (Admin →
 * Einstellungen → Fair Use). Beim Speichern wird der aktuelle Stand als **volle**
 * Vereinbarung für diesen Kunden festgeschrieben — spätere Änderungen am Hausstandard
 * wirken dann nicht mehr hinein. Das ist Absicht: Was mit einem Kunden vereinbart ist,
 * darf sich nicht ändern, weil jemand später an der Vorlage dreht.
 */
export function FairUseModal({
  opened,
  onClose,
  customerId,
  customerName,
  policy,
  isOwn,
  includedHoursPerMonth,
  canSeeCalculation,
  queryKey,
}: {
  opened: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  policy: FairUsePolicy;
  isOwn: boolean;
  includedHoursPerMonth: number | null;
  /** ADMIN/TEAMLEAD: Beträge hinter der Stundenzahl sichtbar und änderbar. */
  canSeeCalculation: boolean;
  /** Query der Kundenakte, die nach dem Speichern neu geladen wird. */
  queryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const catalogQuery = useQuery({
    queryKey: ['fair-use-catalog'],
    queryFn: () => api.getFairUseCatalog(),
    staleTime: 10 * 60_000,
    enabled: opened,
  });

  const [draft, setDraft] = useState<FairUsePolicy>(policy);
  const [hours, setHours] = useState<number | ''>(includedHoursPerMonth ?? '');

  // Beim Öffnen den gespeicherten Stand übernehmen — ein halb bearbeiteter Entwurf aus
  // einem früheren Öffnen wäre bei einer Vertragsangabe die schlechtere Wahl.
  useEffect(() => {
    if (opened) {
      setDraft(policy);
      setHours(includedHoursPerMonth ?? '');
    }
  }, [opened, policy, includedHoursPerMonth]);

  const perSeat = draft.basis === 'PER_SEAT';
  const computedHours =
    perSeat && draft.seats && draft.hoursPerSeat
      ? Math.round(draft.seats * draft.hoursPerSeat * 100) / 100
      : null;
  const effectiveHours = computedHours ?? (hours === '' ? null : Number(hours));

  const save = useMutation({
    mutationFn: async (next: FairUsePolicy | null) => {
      // Kontingent und Vereinbarung sind zwei Endpunkte, aber eine Entscheidung:
      // erst die Zahl, dann der Text, damit beides zusammenpasst.
      //
      // Beim Zurücksetzen bleibt die Stundenzahl stehen: Sie ist die Zahl DIESES Kunden,
      // der Hausstandard kennt gar keine. Sie mit zu löschen würde die Auslastung
      // stillschweigend abschalten.
      if (next != null) await api.updateCustomerContingent(customerId, effectiveHours);
      await api.updateCustomerFairUse(customerId, next);
    },
    onSuccess: (_data, next) => {
      queryClient.invalidateQueries({ queryKey });
      notifications.show({
        title: next == null ? 'Auf Hausstandard zurückgesetzt' : 'Vereinbarung festgeschrieben',
        message: '',
        color: 'green',
      });
      onClose();
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title={`Fair Use — ${customerName}`} size="lg">
      <Stack gap="md">
        <Group gap="xs">
          <Badge variant="light" color={isOwn ? 'teal' : 'gray'}>
            {isOwn ? 'Eigene Vereinbarung' : 'Hausstandard'}
          </Badge>
          {!isOwn && (
            <Text size="xs" c="dimmed">
              Beim Speichern wird der Stand für diesen Kunden festgeschrieben.
            </Text>
          )}
        </Group>

        <Alert color="blue" variant="light" icon={<IconInfoCircle size={14} />} p="xs">
          <Text size="xs">
            Diese Angaben schreiben keine Rechnung. Sie ordnen ein: Das Portal zeigt im
            Rückblick, wann der Rahmen aufgebraucht war und was danach noch reinkam — damit im
            Kundengespräch klar ist, worüber wirklich zu reden ist.
          </Text>
        </Alert>

        <div>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={4}>
            Bemessung
          </Text>
          <SegmentedControl
            size="xs"
            value={draft.basis}
            onChange={(value) => setDraft({ ...draft, basis: value as FairUsePolicy['basis'] })}
            data={[
              { value: 'FLAT', label: 'Feste Stundenzahl' },
              { value: 'PER_SEAT', label: 'Je Arbeitsplatz' },
            ]}
          />
          <Group gap="sm" mt="xs" align="flex-end" wrap="wrap">
            {perSeat && (
              <>
                <NumberInput
                  size="xs"
                  label="Arbeitsplätze"
                  w={130}
                  min={1}
                  value={draft.seats ?? ''}
                  onChange={(v) => setDraft({ ...draft, seats: typeof v === 'number' ? v : null })}
                />
                <NumberInput
                  size="xs"
                  label="Stunden je Arbeitsplatz"
                  w={170}
                  min={0}
                  step={0.25}
                  decimalScale={2}
                  value={draft.hoursPerSeat ?? ''}
                  onChange={(v) =>
                    setDraft({ ...draft, hoursPerSeat: typeof v === 'number' ? v : null })
                  }
                />
              </>
            )}
            <NumberInput
              size="xs"
              label="Kontingent"
              w={150}
              min={0}
              step={0.5}
              decimalScale={2}
              suffix=" h/Monat"
              readOnly={perSeat}
              value={computedHours ?? hours}
              onChange={(v) => setHours(typeof v === 'number' ? v : '')}
              description={perSeat ? 'aus Arbeitsplätzen berechnet' : undefined}
            />
          </Group>
        </div>

        <Divider />

        <FairUsePolicyFields
          value={draft}
          onChange={setDraft}
          canSeeCalculation={canSeeCalculation}
          showCustomerAmounts
          included={catalogQuery.data?.included ?? []}
          excluded={catalogQuery.data?.excluded ?? []}
          holidayRegions={catalogQuery.data?.holidayRegions ?? []}
          hoursForPreview={effectiveHours}
          onUseComputedHours={(value) => {
            setHours(value);
            setDraft((d) => ({ ...d, basis: 'FLAT' }));
          }}
        />

        <Group justify="space-between">
          {isOwn ? (
            <Button
              variant="subtle"
              color="red"
              size="compact-sm"
              loading={save.isPending}
              onClick={() => save.mutate(null)}
            >
              Auf Hausstandard zurücksetzen
            </Button>
          ) : (
            <span />
          )}
          <Group gap="xs">
            <Button variant="subtle" size="compact-sm" onClick={onClose}>
              Abbrechen
            </Button>
            <Button size="compact-sm" loading={save.isPending} onClick={() => save.mutate(draft)}>
              Festschreiben
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
